<template>
  <div>
    <div
      v-if="visibleBanner"
      class="ld-lw-connect-banner"
    >
      <v-layout
        justify-center
        align-center
        :style="{ 'background-color': color.background  }"
      >
        <span v-html="text"></span>
        <v-btn
          id="lw-connect-banner-connect"
          class="btn-connect ml-3"
          :color="color.actionColor"
          @click="handleConnect"
        >Link Account</v-btn>
        <v-btn
          id="lw-connect-banner-learnmore"
          flat
          dark
          :color="color.foreground"
          @click="handleLearnMore"
        >
          Learn more
        </v-btn>
        <v-btn
          id="lw-connect-banner-hide"
          icon
          small
          @click="handleHide"
        >
          <v-icon class="white-icon">close</v-icon>
        </v-btn>
      </v-layout>
    </div>
    <v-dialog
      v-if="visibleLearnMoreDialog"
      v-model="visibleLearnMoreDialog"
      persistent
      max-width="700"
    >
      <v-card>
        <div class="close-btn-container mr-2">
          <v-btn
            class="icon-btn ma-0 pa-0"
            flat="flat"
            icon="icon"
            @click="handleLearnMore"
          >
            <v-icon
              class="ma-0 pa-0"
              size="20"
            >close</v-icon>
          </v-btn>
        </div>
        <v-layout
          class="dialog-container px-5 pt-3"
          wrap
        >
          <v-flex class="mt-3 xs4 ld-icon">
            <v-img
              src="/static/img/ld-powered-lw-logo.svg"
              height="40"
              contain
            ></v-img>
          </v-flex>
          <v-flex class="mt-3 xs8 title">
            Connect your LionDesk account
          </v-flex>
          <v-flex class="body mt-3 xs12">
            <p class="description">
              Did you know? When you create a <a
                    href="https://www.lwolf.com/"
                    target="_blank"
                  >Lone Wolf</a> account for your LionDesk software, you can connect it with your other Lone Wolf software for a better experience. Here’s how.
            </p>
          </v-flex>
          <v-flex class="body xs12">
            <ol>
              <li>Sign up for your Lone Wolf account</li>
              <li>Follow the on-screen instructions to link your Lone Wolf and LionDesk accounts</li>
            </ol>
          </v-flex>
          <v-flex class="body mt-3 xs12">
            <span class="subtitle">What does connecting your software do?</span>
            <ul>
              <li><strong>Simpler logging in:</strong> You can access all Lone Wolf software with one set of credentials.</li>
              <li><strong>Better security:</strong> Lone Wolf accounts include extra layers of security to protect your data.</li>
              <li><strong>Full access:</strong> Carry your workflow across other Lone Wolf software like Cloud CMA, Boost, Spacio, Authentisign, and more. </li>
            </ul>
          </v-flex>
          <v-flex class="body mt-3 xs12">
            <span class="description">Ready for a new way to use your LionDesk?</span>
          </v-flex>
          <v-flex class="xs12 my-4">
            <v-btn
              id="lw-connect-banner-setup"
              class="btn-action"
              color="primary"
              @click="handleConnect"
              data-cy="submit-button"
            >Connect your account</v-btn>
          </v-flex>
        </v-layout>
      </v-card>
    </v-dialog>
    <v-dialog
      v-if="visibleEndFlowDialog"
      v-model="visibleEndFlowDialog"
      persistent
      max-width="600"
    >
      <v-card>
        <ld-title-bar dark>
          Connected Lone Wolf Account
        </ld-title-bar>
        <v-card-text>
          <v-layout
            class="dialog-container"
            wrap
          >
            <v-flex class="body mt-3 xs12">
              <p class="description">
                You're all set!
              </p>
            </v-flex>
            <v-flex class="body mt-2 xs12">
              <p class="description">
                You can change your Lone Wolf profile by going to “Home” using the app switcher at the top left corner of the screen.
              </p>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            id="lw-connect-banner-end-dialog-close"
            flat
            color="primary"
            @click="handleCloseLastDialog"
          >Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { HC_PROPS } from 'lion-vue-src/_globals';
import LdTitleBar from '../LDTitleBar';
import { startOfDay, addDays } from 'date-fns';

const TRIAL_STATUS_LIST = ['trial', 'trial_expired'];

const SSO_PLANS_LIST = [
  'plan-liondesk-CRMLS-monthly',
  'plan-liondesk-v2-CAR-monthly',
  'plan-liondesk-SDMLS-monthly',
  'plan-liondesk-CCAR-monthly',
  'plan-liondesk-BayEast-monthly',
  'plan-liondesk-promote-agent-bundle-monthly',
  'plan-liondesk-leadsplus-monthly',
];

export default {
  components: { LdTitleBar },
  props: {
    user: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      visibleBanner: false,
      visibleLearnMoreDialog: false,
      visibleEndFlowDialog: false,
      color: {
        foreground: 'white',
        actionColor: '#A64951',
        background: '#E4919F',
      },
    };
  },
  mounted() {
    this.showBanner();
    this.showEndFlowDialog();
  },
  methods: {
    showBanner() {
      if (!this.user) {
        return;
      }
      
      const hideBannerCookie = window.$cookies.get('hideConnectLWBanner');
      if (hideBannerCookie) {
        const displayDate = startOfDay(addDays(new Date(hideBannerCookie), 1));
        if (new Date() < displayDate) {
          this.visibleBanner = false;
          return;
        }
        window.$cookies.remove('hideConnectLWBanner', null, HC_PROPS.MAIN_DOMAIN);
      }

      this.visibleBanner = this.allowedLWAConnection && !this.user.lwa_id; 
      this.$emit('show', this.visibleBanner);
    },
    showEndFlowDialog() {
      if (!this.user) {
        return;
      }

      const isPromoteLWAFlow = this.$route.query['plwa'] === 'connected';
      this.visibleEndFlowDialog = isPromoteLWAFlow && !this.visibleBanner && !!this.user.lwa_id;
      if (this.visibleEndFlowDialog) {
        this.$nextTick(() => {
          const ulMenu = document.querySelector('#lwt-unified-navbar__application-switcher-menu-toggle');
          if (ulMenu) ulMenu.focus();
        });
        
      }
    },
    getRedirectURL() {
      return `${HC_PROPS.ACCOUNTS_APP_URL}/connect-lone-wolf-account?ref=${encodeURIComponent(
        `${location.origin}?plwa=connected`,
      )}&state=connect`;
    },
    handleConnect() {
      window.location.href = this.getRedirectURL();
    },
    handleLearnMore() {
      this.visibleLearnMoreDialog = !this.visibleLearnMoreDialog;
    },
    handleCloseLastDialog() {
      this.visibleEndFlowDialog = false;
    },
    handleHide() {
      this.visibleBanner = false;
      window.$cookies.set('hideConnectLWBanner', new Date(), null, null, HC_PROPS.MAIN_DOMAIN);
      this.$emit('show', this.visibleBanner);
    }
  },
  computed: {
    text() {
      return `Connect your LionDesk to other Lone Wolf software today for a better experience!`;
    },
    allowedLWAConnection() {
      if (!this.user) {
        return false;
      }

      if (!this.user.subscriptionOverview) {
        return false;
      }

      const { subscriptionOverview } = this.user;

      const isTrial = TRIAL_STATUS_LIST.includes(subscriptionOverview.txt_status);
      if (isTrial) return false;

      const activeSubscription =
        subscriptionOverview.details && subscriptionOverview.details.txt_status === 'active';
      if (!activeSubscription) return false;

      const isSSOSubscription = subscriptionOverview.active_items.some(str =>
        SSO_PLANS_LIST.some(subStr => str.includes(subStr)),
      );
      return !isSSOSubscription;
    },
  },
  watch: {
    visibleBanner: {
      handler() {
        this.$emit('show', this.visibleBanner);
      },
    },
    user: {
      handler() {
        this.showBanner();
        this.showEndFlowDialog();
      },
    },
  },
};
</script>
<style lang="scss" scoped>
$banner-font-size: 16px;
.ld-lw-connect-banner {
  line-height: 1;
  height: 56px;
  color: white;
  font-weight: bold;
  font-size: $banner-font-size;
  width: 100%;
  padding: 6px 24px 2px;

  .layout {
    padding: 0 12px;
    height: 100%;
  }
  .divider {
    height: 30px;
    width: 1px;
    border-left: 1px solid rgba(0, 0, 0, 0.27);
    margin: auto 0;
  }
  .v-btn {
    font-size: 14px;
    margin-right: 0px;
    margin-left: 0px;
  }
  .btn-connect {
    font-weight: bold;
    color: white !important;
  }
  .v-btn.v-btn--icon.v-btn--small {
    margin-left: 12px;
  }
}

.dialog-container {
  justify-content: center;
  text-align: center;
  .body {
    text-align: start;
    font-size: 14px;

    .description {
      font-size: 16px;
    }
  }

  .btn-action {
    color: white !important;
  }
}

.title {
  font-size: 20px !important;
  font-weight: bolder;
  text-align: left;
  padding-top: 16px;
}

.subtitle {
  font-weight: bold;
}

.close-btn-container {
  position: absolute;
  right: 0;
}
.ld-icon {
  margin-left: -56px;
}

.white-icon {
  color: white !important;
}
</style>
